import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core'
import { ChartModule } from 'primeng/chart'

export interface LegalRepaymentsChartBar {
  month: string
  amount: number
  count: number
}

@Component({
  selector: 'app-legal-repayments-chart',
  standalone: true,
  imports: [CommonModule, ChartModule],
  templateUrl: './legal-repayments-chart.component.html',
  styleUrl: './legal-repayments-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalRepaymentsChartComponent implements AfterViewInit {
  private readonly monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' })
  private readonly compactCurrencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', notation: 'compact', maximumFractionDigits: 1,
  })
  private _bars: LegalRepaymentsChartBar[] = []

  @Input({ required: true }) set bars(value: LegalRepaymentsChartBar[]) {
    this._bars = value ?? []
    this.chartData = {
      labels: this._bars.map((bar) => this.formatMonth(bar.month)),
      datasets: [{
        label: 'Payment amount',
        data: this._bars.map((bar) => bar.amount),
        backgroundColor: '#0dddc4',
        borderColor: '#0dddc4',
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 60,
        categoryPercentage: .96,
        barPercentage: .96,
      }],
    }
    this.chartOptions = {
      ...this.chartOptions,
      scales: {
        ...this.chartOptions.scales,
        y: { ...this.chartOptions.scales.y, max: this.yAxisTicks[0] },
      },
    }
    this.scheduleScrollToLatestMonth()
  }

  get bars(): LegalRepaymentsChartBar[] {
    return this._bars
  }

  private _loading = false
  @Input() set loading(value: boolean) {
    this._loading = value
    if (!value) this.scheduleScrollToLatestMonth()
  }

  get loading(): boolean {
    return this._loading
  }

  @ViewChild('chartScroll') private chartScroll?: ElementRef<HTMLElement>
  @ViewChild('metricsScroll') private metricsScroll?: ElementRef<HTMLElement>
  chartData: any = { labels: [], datasets: [] }
  chartOptions: any = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => ` ${this.formatCurrency(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Month' },
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45, autoSkip: true, maxTicksLimit: 18 },
      },
      y: {
        beginAtZero: true,
        title: { display: false },
        ticks: { display: false },
      },
    },
  }

  formatMonth(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})$/)
    return match
      ? this.monthFormatter.format(new Date(Number(match[1]), Number(match[2]) - 1, 1))
      : value
  }

  get barsByNewestMonth(): LegalRepaymentsChartBar[] {
    return [...this.bars].reverse()
  }

  formatAmount(value: number): string {
    return this.formatCurrency(value)
  }

  formatCount(value: number): string {
    return new Intl.NumberFormat('en-GB').format(value)
  }

  get chartWidth(): number {
    return Math.max(960, this.bars.length * 58)
  }

  get yAxisTicks(): number[] {
    const maximum = Math.max(...this.bars.map((bar) => bar.amount), 0)
    if (maximum === 0) return [0]
    const step = this.niceNumber(maximum / 4)
    const ceiling = Math.ceil(maximum / step) * step
    return [ceiling, ceiling - step, ceiling - step * 2, ceiling - step * 3, 0]
  }

  formatAxisAmount(value: number): string {
    return this.compactCurrencyFormatter.format(value)
  }

  ngAfterViewInit(): void {
    this.scheduleScrollToLatestMonth()
  }

  onChartScroll(event: Event): void {
    const chart = event.currentTarget as HTMLElement
    const metrics = this.metricsScroll?.nativeElement
    if (!metrics) return
    this.syncScrollPosition(chart, metrics, 'horizontal', 'vertical', true)
  }

  onMetricsScroll(event: Event): void {
    const metrics = event.currentTarget as HTMLElement
    const chart = this.chartScroll?.nativeElement
    if (!chart) return
    this.syncScrollPosition(metrics, chart, 'vertical', 'horizontal', true)
  }

  private scheduleScrollToLatestMonth(): void {
    setTimeout(() => {
      const container = this.chartScroll?.nativeElement
      const metrics = this.metricsScroll?.nativeElement
      if (container && !this.loading) container.scrollLeft = container.scrollWidth
      if (metrics && !this.loading) metrics.scrollTop = 0
    })
  }

  private syncScrollPosition(
    source: HTMLElement,
    target: HTMLElement,
    sourceDirection: 'horizontal' | 'vertical',
    targetDirection: 'horizontal' | 'vertical',
    reverse = false,
  ): void {
    const sourcePosition = sourceDirection === 'horizontal' ? source.scrollLeft : source.scrollTop
    const sourceMaximum = sourceDirection === 'horizontal'
      ? source.scrollWidth - source.clientWidth
      : source.scrollHeight - source.clientHeight
    const targetMaximum = targetDirection === 'horizontal'
      ? target.scrollWidth - target.clientWidth
      : target.scrollHeight - target.clientHeight
    if (sourceMaximum <= 0 || targetMaximum <= 0) return

    const progress = sourcePosition / sourceMaximum
    const targetPosition = (reverse ? 1 - progress : progress) * targetMaximum
    const currentPosition = targetDirection === 'horizontal' ? target.scrollLeft : target.scrollTop
    if (Math.abs(currentPosition - targetPosition) < 1) return
    if (targetDirection === 'horizontal') target.scrollLeft = targetPosition
    else target.scrollTop = targetPosition
  }

  private niceNumber(value: number): number {
    const magnitude = 10 ** Math.floor(Math.log10(value))
    const normalized = value / magnitude
    const rounded = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
    return rounded * magnitude
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value)
  }
}
